/**
 * Scrubbing applied to everything the Sentry browser and React Native SDKs send.
 *
 * The event and breadcrumb types are structural rather than imported, so the
 * web app (`@sentry/react` 7) and the native app (`@sentry/react-native`) can
 * both pass their own SDK's objects through, and this package takes no Sentry
 * dependency.
 *
 * The SDK attaches `document.location.href` to every event — errors and
 * performance transactions alike — via its default HttpContext integration, and
 * auto-instrumented fetch/XHR spans carry the request URL in their description.
 * Under HIPAA we cannot ship any of that verbatim:
 *
 * - `/signIn/verifyOtp?email=…&token_hash=…` and `/forgotPassword?token_hash=…`
 *   put a member's email address and a live single-use auth token in the URL.
 * - Supabase's implicit auth flow returns tokens in the URL fragment.
 * - `document.referrer` carries the previous page's query string, so navigating
 *   away from one of those routes leaks it a second time.
 *
 * Our route params are all opaque UUIDs, so the path on its own is enough to
 * debug with. Everything after it goes.
 */

/**
 * Drops the query string and fragment, keeping the path.
 *
 * This is an allowlist rather than a denylist on purpose. The params that carry
 * an identifier today are `email` and `token_hash`, but on this app any new one
 * is a member identifier until proven otherwise, and nothing in review would
 * catch a sensitive one being added to a route.
 *
 * Splitting the string rather than parsing a `URL` keeps relative URLs relative
 * (fetch breadcrumbs record `/backend/…`) and cannot throw on the malformed
 * values that reach us from breadcrumb data typed as `any`.
 */
export function scrubUrl(url: string): string {
  return url.split(/[?#]/)[0]
}

/** The fields of a breadcrumb this reads or rewrites. */
export interface ScrubbableBreadcrumb {
  category?: string
  data?: Record<string, unknown>
}

/** The URL-bearing fields of an error event or transaction. */
export interface ScrubbableEvent {
  request?: {
    url?: string
    headers?: Record<string, string>
    query_string?: unknown
  }
  spans?: UrlBearingSpan[]
}

/** Keys under `breadcrumb.data` that hold a URL. */
const URL_DATA_KEYS = ['url', 'from', 'to']

/**
 * Runs on every breadcrumb before it is recorded, so the scrubbed value is what
 * sits in the buffer — a breadcrumb dropped here can never reach an event.
 */
export function scrubBreadcrumb<B extends ScrubbableBreadcrumb>(breadcrumb: B): B | null {
  // Don't capture console logs - we want to explicitly leave breadcrumbs due to
  // HIPAA concerns.
  if (breadcrumb.category === 'console') {
    return null
  }

  const data = breadcrumb.data
  if (data) {
    // fetch/xhr breadcrumbs put the request URL in `url`; navigation breadcrumbs
    // record `from`/`to`, which is how a magic-link landing leaks even after the
    // user has clicked through to another route.
    for (const key of URL_DATA_KEYS) {
      const value = data[key]
      if (typeof value === 'string') {
        data[key] = scrubUrl(value)
      }
    }
  }

  return breadcrumb
}

/** The URL-bearing fields of a span, structurally typed so this stays valid across SDK majors. */
interface UrlBearingSpan {
  description?: string
  data?: Record<string, unknown>
}

/**
 * Runs on every error event and every performance transaction. Mutates in place
 * and returns the event, which is the shape Sentry's hooks expect.
 */
export function scrubEvent<E extends ScrubbableEvent>(event: E): E {
  if (event.request) {
    if (typeof event.request.url === 'string') {
      event.request.url = scrubUrl(event.request.url)
    }
    // The referrer is the URL of the page navigated away from, so it leaks the
    // auth-landing query string onto the *next* page's events.
    const referer = event.request.headers?.Referer
    if (typeof referer === 'string') {
      event.request.headers!.Referer = scrubUrl(referer)
    }
    delete event.request.query_string
  }

  // A transaction event carries one span per instrumented fetch/XHR, with the
  // request URL in the description ("GET https://…") and often repeated in
  // `data.url`. No query string we send carries PHI today — PostgREST filters
  // are UUIDs and free-text search is POSTed in the body — but that is a
  // property of the current call sites, not something enforced. The first `?q=`
  // someone adds would land here silently.
  const spans: UrlBearingSpan[] | undefined = event.spans
  spans?.forEach((span) => {
    if (typeof span.description === 'string') {
      span.description = scrubUrl(span.description)
    }
    const url = span.data?.url
    if (typeof url === 'string') {
      span.data!.url = scrubUrl(url)
    }
  })

  return event
}
